import "@arco-design/web-react/es/ConfigProvider/style";
import _ConfigProvider from "@arco-design/web-react/es/ConfigProvider";
import "@arco-design/web-react/es/Spin/style";
import _Spin from "@arco-design/web-react/es/Spin";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import './style/tailwind.css';
import './style/global.less';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import zhCN from '@arco-design/web-react/es/locale/zh-CN';
import enUS from '@arco-design/web-react/es/locale/en-US';
import { Router, Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { useRequest } from 'ahooks';
import store from './store/store';
import PageLayout from './layout';
import { GlobalContext } from './context';
import Login from './pages/login';
import checkLogin from './utils/checkLogin';
import changeTheme from './utils/changeTheme';
import appHistory from './utils/history';
import useStorage from './utils/useStorage';
import { loadRuntimeConfig } from './utils/loadRuntimeConfig';
import { initPrefix } from './utils/prefix';
import { getAllDicts, getUserMenuKeys } from './services';
import './mock';

// 全局提示配置：同时只能有一个提示
_Message.config({
  maxCount: 1
});
function AppRoutes({
  initBizData
}) {
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;
    const fromLogin = previousPathname === '/login' && location.pathname !== '/login';
    if (!fromLogin || !checkLogin()) {
      return;
    }
    initBizData().catch(error => {
      console.error(error);
    });
  }, [initBizData, location.pathname]);
  return /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    path: "/login",
    component: Login
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/",
    render: () => checkLogin() ? /*#__PURE__*/React.createElement(PageLayout, null) : /*#__PURE__*/React.createElement(Redirect, {
      to: "/login"
    })
  }));
}
function Index() {
  const [lang, setLang] = useStorage('arco-lang', 'zh-CN');
  const [theme, setTheme] = useStorage('arco-theme', 'light');
  const [appReady, setAppReady] = useState(false);
  function getArcoLocale() {
    switch (lang) {
      case 'zh-CN':
        return zhCN;
      case 'en-US':
        return enUS;
      default:
        return zhCN;
    }
  }

  // 加载运行时配置
  const loadRuntimeConfigApi = useRequest(loadRuntimeConfig, {
    manual: true,
    onSuccess: appConfig => {
      initPrefix(appConfig);
      store.dispatch({
        type: 'update-appConfig',
        payload: {
          appConfig
        }
      });
    }
  });
  const {
    runAsync: runLoadRuntimeConfig
  } = loadRuntimeConfigApi;

  // 加载所有枚举
  const getAllDictsApi = useRequest(getAllDicts, {
    manual: true,
    onSuccess: dicts => {
      console.log('dicts', dicts);
      store.dispatch({
        type: 'update-dicts',
        payload: {
          dicts
        }
      });
    }
  });
  const {
    runAsync: runGetAllDicts
  } = getAllDictsApi;

  // // 加载人员列表
  // const getPersonListApi = useRequest(getPersonList, {
  //   manual: true,
  //   onSuccess: (response) => {
  //     console.log('response', response);
  //     const currentUserInfo = (store.getState() as GlobalState).userInfo;
  //     const user = {
  //       ...currentUserInfo,
  //       person: response.list[0],
  //     };

  //     store.dispatch({
  //       type: 'update-userInfo',
  //       payload: {
  //         userInfo: user,
  //       },
  //     });
  //   },
  // });
  // const { runAsync: runGetPersonList } = getPersonListApi;

  const getCurrentUserId = useCallback(() => {
    const state = store.getState();
    return state.userInfo?.userId;
  }, []);

  // 初始化业务数据
  const initBizData = useCallback(async () => {
    const userId = getCurrentUserId();
    try {
      await runGetAllDicts();
    } catch (err) {
      console.error('获取字典失败:', err);
    }
    if (!userId) {
      return;
    }
    try {
      const menuKeys = await getUserMenuKeys(userId);
      store.dispatch({
        type: 'update-menuKeys',
        payload: {
          menuKeys
        }
      });
    } catch (err) {
      console.error('获取菜单失败:', err);
    }
  }, [getCurrentUserId, runGetAllDicts]);
  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      try {
        if (checkLogin()) {
          store.dispatch({
            type: 'update-userInfo',
            payload: {
              userLoading: false
            }
          });
        }
        await runLoadRuntimeConfig();
        if (checkLogin()) {
          await initBizData();
        }
        if (!mounted) {
          return;
        }
        setAppReady(true);
      } catch (error) {
        console.error(error);
      }
    }
    bootstrap();
    return () => {
      mounted = false;
    };
  }, [initBizData, runLoadRuntimeConfig]);
  useEffect(() => {
    changeTheme(theme);
  }, [theme]);
  const contextValue = {
    lang,
    setLang,
    theme,
    setTheme
  };
  return /*#__PURE__*/React.createElement(Router, {
    history: appHistory
  }, /*#__PURE__*/React.createElement(_ConfigProvider, {
    locale: getArcoLocale(),
    componentConfig: {
      Card: {
        bordered: false
      },
      List: {
        bordered: false
      },
      Table: {
        border: false
      },
      Spin: {
        size: 30
      }
    }
  }, /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(GlobalContext.Provider, {
    value: contextValue
  }, appReady ? /*#__PURE__*/React.createElement(AppRoutes, {
    initBizData: initBizData
  }) : /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement(_Spin, null))))));
}
ReactDOM.render(/*#__PURE__*/React.createElement(Index, null), document.getElementById('root'));